/**
 * Merchant Products console — iter 45 · Phase B.
 * Supports: manual create, paste-URL scrape, catalogue list w/ search + filter.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Package, Plus, Link as LinkIcon, Search, Loader2, Edit, Trash2,
  Sparkles, Image as ImageIcon, RefreshCw, X, ExternalLink,
  Wand2, BarChart3, CheckSquare, Square, ArrowRight, TrendingUp,
  LayoutGrid, FolderTree, ChevronDown, ChevronRight, Pencil, Check,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { cad } from "@/lib/pricing";
import { useAiEntitlement } from "@/hooks/useAiEntitlement";
import { AiUpgradeCta } from "@/components/merchant/AiUpgrade";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

function fmt(minor, currency = "CAD") {
  return currency === "CAD" ? cad((minor || 0) / 100)
    : ((minor || 0) / 100).toLocaleString("en-CA", { style: "currency", currency });
}

export default function MerchantProductsPage() {
  const { aiEnabled } = useAiEntitlement();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [creator, setCreator] = useState(null); // {mode:"manual"|"url"}
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [perfPid, setPerfPid] = useState(null);
  const [view, setView] = useState("grid");   // "grid" | "categories"
  const [categories, setCategories] = useState(null);
  const [inferring, setInferring] = useState(false);
  const [expanded, setExpanded] = useState({});

  const loadCategories = useCallback(async () => {
    try {
      const r = await api.get("/v1/merchant/products/categories");
      setCategories(r.data);
    } catch (e) {
      // Categories are optional taxonomy metadata. Surface transient
      // failures via debug log so we can spot backend outages without
      // blocking the Products page from rendering.
      // eslint-disable-next-line no-console
      console.debug("[products] categories load failed:", e?.message || e);
    }
  }, []);
  useEffect(() => { if (view === "categories") loadCategories(); }, [view, loadCategories, rows]);

  const inferCategories = async () => {
    setInferring(true);
    try {
      const r = await api.post("/v1/merchant/products/infer-categories", {});
      if (r.data.updated > 0) toast.success(`Categorised ${r.data.updated} product${r.data.updated > 1 ? "s" : ""}.`);
      else toast.info(r.data.detail || "Nothing to categorize.");
      await load();
      await loadCategories();
    } catch (e) {
      toast.error(e.response?.data?.detail?.detail || "AI categorization failed.");
    } finally { setInferring(false); }
  };

  const selectCategory = (product_ids) => {
    setSelected((prev) => {
      const next = new Set(prev);
      product_ids.forEach((id) => next.add(id));
      return next;
    });
  };
  const deselectCategory = (product_ids) => {
    setSelected((prev) => {
      const next = new Set(prev);
      product_ids.forEach((id) => next.delete(id));
      return next;
    });
  };

  const toggle = (pid) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(pid)) next.delete(pid); else next.add(pid);
    return next;
  });
  const clearSel = () => setSelected(new Set());
  const selectAll = () => setSelected(new Set(rows.map((r) => r.id)));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/v1/merchant/products?limit=200${q ? `&q=${encodeURIComponent(q)}` : ""}`);
      setRows(r.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load products");
    } finally { setLoading(false); }
  }, [q]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const handler = () => { load(); if (view === "categories") loadCategories(); };
    window.addEventListener("cyannect:products-reload", handler);
    return () => window.removeEventListener("cyannect:products-reload", handler);
  }, [load, loadCategories, view]);

  const archive = async (pid) => {
    if (!window.confirm("Archive this product?")) return;
    try {
      await api.delete(`/v1/merchant/products/${pid}`);
      toast.success("Product archived.");
      load();
    } catch { toast.error("Archive failed"); }
  };

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto" data-testid="merch-products">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="pf-chip pf-chip-cyan w-fit"><Package className="w-3 h-3" /> Products</div>
          <h1 className="font-display text-2xl md:text-3xl text-slate-900 mt-2">Your product catalogue</h1>
          <p className="text-xs text-slate-500 mt-1">Attach offers to specific products. Import from Shopify/Woo or add manually.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setCreator({ mode: "url" })}
                  className="px-3 py-1.5 rounded-full text-xs border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1"
                  data-testid="products-btn-paste-url">
            <LinkIcon className="w-3 h-3" /> Paste product URL
          </button>
          <button onClick={() => setCreator({ mode: "manual" })}
                  className="pf-btn-primary px-4 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1"
                  data-testid="products-btn-manual">
            <Plus className="w-3 h-3" /> Add product
          </button>
        </div>
      </header>

      {/* Bulk-select action bar — appears when ≥ 1 product is selected. */}
      {selected.size > 0 && (
        <div className="pf-card p-3 flex items-center gap-3 bg-gradient-to-r from-violet-50 to-cyan-50 border-cyan-200/60 sticky top-4 z-10"
             data-testid="products-bulk-bar">
          <div className="text-sm text-slate-700">
            <span className="font-semibold text-slate-900">{selected.size}</span> product{selected.size > 1 ? "s" : ""} selected
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={selectAll} className="text-xs text-slate-600 hover:text-slate-900" data-testid="products-select-all">
              Select all {rows.length}
            </button>
            <button onClick={clearSel} className="text-xs text-slate-600 hover:text-slate-900" data-testid="products-clear-sel">
              Clear
            </button>
            {aiEnabled ? (
            <button onClick={() => setBulkOpen(true)}
                    className="pf-btn-primary px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1"
                    data-testid="products-bulk-open">
              <Wand2 className="w-3 h-3" /> Bulk offer with AI
            </button>
            ) : <AiUpgradeCta testid="products-bulk-locked" />}
          </div>
        </div>
      )}

      <div className="flex gap-2 items-center flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or SKU…"
                 className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-full text-sm"
                 data-testid="products-search" />
        </div>
        <div className="inline-flex bg-slate-100 rounded-full p-1" data-testid="products-view-toggle">
          <button onClick={() => setView("grid")}
                  className={`px-3 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1 transition
                    ${view === "grid" ? "bg-white text-slate-900 shadow" : "text-slate-500"}`}
                  data-testid="products-view-grid">
            <LayoutGrid className="w-3 h-3" /> Grid
          </button>
          <button onClick={() => setView("categories")}
                  className={`px-3 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1 transition
                    ${view === "categories" ? "bg-white text-slate-900 shadow" : "text-slate-500"}`}
                  data-testid="products-view-categories">
            <FolderTree className="w-3 h-3" /> By category
          </button>
        </div>
      </div>

      {loading ? (
        <div className="pf-card p-8 text-center text-slate-500" data-testid="products-loading">
          <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Loading products…
        </div>
      ) : rows.length === 0 ? (
        <div className="pf-card p-8 text-center" data-testid="products-empty">
          <Package className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <div className="text-sm text-slate-600 font-medium">No products yet</div>
          <div className="text-xs text-slate-500 mt-1">Paste a product URL or add one manually to get started.</div>
        </div>
      ) : view === "categories" ? (
        <CategoryView
          categories={categories}
          products={rows}
          selected={selected}
          onSelectCategory={selectCategory}
          onDeselectCategory={deselectCategory}
          onTogglePid={toggle}
          onExpand={setExpanded}
          expanded={expanded}
          onInfer={inferCategories}
          inferring={inferring}
          onPerf={setPerfPid}
          onArchive={archive}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((p) => {
            const isSel = selected.has(p.id);
            return (
            <div key={p.id}
                 className={`pf-card p-4 flex gap-3 relative transition ${isSel ? "ring-2 ring-cyan-500" : ""}`}
                 data-testid={`product-${p.id}`}>
              <button onClick={() => toggle(p.id)}
                       className="absolute top-2 right-2 text-slate-400 hover:text-cyan-700"
                       data-testid={`product-select-${p.id}`}
                       aria-label={isSel ? "Deselect" : "Select"}>
                {isSel ? <CheckSquare className="w-4 h-4 text-cyan-600" />
                       : <Square className="w-4 h-4" />}
              </button>
              <div className="w-16 h-16 rounded-lg bg-slate-100 overflow-hidden shrink-0 flex items-center justify-center">
                {p.image ? <img src={p.image} alt="" className="w-full h-full object-cover" />
                         : <ImageIcon className="w-6 h-6 text-slate-300" />}
              </div>
              <div className="flex-1 min-w-0 pr-6">
                <div className="text-sm text-slate-900 font-semibold truncate">{p.name}</div>
                <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                  {p.sku && <span className="font-mono">{p.sku}</span>}
                  {p.category && <span className="pf-chip pf-chip-neutral">{p.category}</span>}
                  {p.provider && p.provider !== "manual" && <span className="pf-chip pf-chip-cyan">{p.provider}</span>}
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <div className="tabular-nums text-sm text-slate-900 font-medium">
                    {p.price_minor ? fmt(p.price_minor, p.currency) : "—"}
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setPerfPid(p.id)}
                            className="text-slate-400 hover:text-cyan-700 p-1"
                            title="View performance"
                            data-testid={`product-perf-${p.id}`}>
                      <BarChart3 className="w-3.5 h-3.5" />
                    </button>
                    {p.url && (
                      <a href={p.url} target="_blank" rel="noreferrer"
                         className="text-slate-400 hover:text-slate-700 p-1" title="Open URL">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                    <button onClick={() => archive(p.id)}
                            className="text-slate-400 hover:text-rose-700 p-1" title="Archive"
                            data-testid={`product-archive-${p.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                {p.active_offers > 0 && (
                  <div className="mt-1 text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">
                    {p.active_offers} active offer{p.active_offers > 1 ? "s" : ""}
                  </div>
                )}
              </div>
            </div>
          );})}
        </div>
      )}

      <CreatorModal open={!!creator} mode={creator?.mode}
                     onOpenChange={(o) => !o && setCreator(null)}
                     onDone={() => { setCreator(null); load(); }} />
      <BulkPromptModal open={bulkOpen}
                        onOpenChange={setBulkOpen}
                        productIds={Array.from(selected)}
                        products={rows.filter((r) => selected.has(r.id))}
                        onDone={() => { setBulkOpen(false); clearSel(); load(); }} />
      <PerformanceModal pid={perfPid} onClose={() => setPerfPid(null)} />
    </div>
  );
}

function CategoryRowHeader({ cat, isExp, allSelected, selectedCount, onSelectAll, onToggleExp }) {
  const [renaming, setRenaming] = useState(false);
  const [value, setValue] = useState(cat.category);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const trimmed = (value || "").trim();
    if (!trimmed || trimmed === cat.category) { setRenaming(false); return; }
    setSaving(true);
    try {
      const r = await api.post("/v1/merchant/products/categories/rename",
        { from: cat.category, to: trimmed });
      toast.success(`Renamed ${r.data.updated} product${r.data.updated === 1 ? "" : "s"} to "${r.data.to}".`);
      setRenaming(false);
      // Trigger a full reload — the parent effect re-fetches on rows change,
      // but here we ping the same effect chain by dispatching a custom event
      // handled by parent-provided callback... simpler: reload the page-level
      // state via a route reload.
      window.dispatchEvent(new Event("cyannect:products-reload"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rename failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="p-3 flex items-center gap-3 hover:bg-slate-50 cursor-pointer"
         onClick={renaming ? undefined : onToggleExp}>
      <button onClick={(e) => { e.stopPropagation(); onSelectAll(); }}
              className="text-slate-400 hover:text-cyan-700"
              data-testid={`products-cat-select-${cat.category}`}
              aria-label={allSelected ? "Deselect all" : "Select all"}>
        {allSelected ? <CheckSquare className="w-4 h-4 text-cyan-600" /> : <Square className="w-4 h-4" />}
      </button>
      {isExp ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      <div className="flex-1 min-w-0 flex items-center gap-2">
        {renaming ? (
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <input value={value} onChange={(e) => setValue(e.target.value)} autoFocus
                   onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setRenaming(false); }}
                   className="px-2 py-1 border border-slate-300 rounded text-sm font-display w-48"
                   data-testid={`cat-rename-input-${cat.category}`} />
            <button onClick={save} disabled={saving}
                    className="text-emerald-600 hover:text-emerald-800 p-1 disabled:opacity-50"
                    data-testid={`cat-rename-save-${cat.category}`}>
              <Check className="w-4 h-4" />
            </button>
            <button onClick={() => setRenaming(false)}
                    className="text-slate-400 hover:text-slate-700 p-1"
                    data-testid={`cat-rename-cancel-${cat.category}`}>
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <>
            <div className="font-display text-slate-900 flex items-center gap-2">
              {cat.category}
              {selectedCount > 0 && (
                <span className="pf-chip pf-chip-cyan text-[10px]">
                  {selectedCount} selected
                </span>
              )}
            </div>
            <button onClick={(e) => { e.stopPropagation(); setRenaming(true); setValue(cat.category); }}
                    className="text-slate-400 hover:text-cyan-700 p-1"
                    data-testid={`cat-rename-start-${cat.category}`} title="Rename category">
              <Pencil className="w-3 h-3" />
            </button>
          </>
        )}
      </div>
      <div className="flex -space-x-2">
        {cat.sample_images.slice(0, 3).map((img, i) => (
          <img key={i} src={img} alt="" className="w-7 h-7 rounded-full border-2 border-white object-cover" />
        ))}
      </div>
      <div className="text-xs text-slate-500 tabular-nums font-semibold">{cat.count} product{cat.count > 1 ? "s" : ""}</div>
    </div>
  );
}

function CategoryView({ categories, products, selected, onSelectCategory, onDeselectCategory,
                        onTogglePid, expanded, onExpand, onInfer, inferring, onPerf, onArchive }) {
  const { aiEnabled } = useAiEntitlement();
  if (!categories) {
    return <div className="p-8 text-center text-sm text-slate-500" data-testid="products-cat-loading">
      <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Loading categories…
    </div>;
  }
  const productById = Object.fromEntries(products.map((p) => [p.id, p]));
  return (
    <div className="space-y-3" data-testid="products-categories">
      <div className="pf-card p-3 flex items-center gap-3 bg-gradient-to-r from-violet-50 to-cyan-50 border-cyan-200/60">
        <Sparkles className="w-4 h-4 text-cyan-600 shrink-0" />
        <div className="text-xs text-slate-700 flex-1 min-w-0">
          {categories.uncategorized_count > 0
            ? <>{categories.uncategorized_count} product{categories.uncategorized_count > 1 ? "s are" : " is"} uncategorized. Let AI infer categories so you can bulk-target them.</>
            : <>Every product has a category. AI can re-scan any time to refine them.</>}
        </div>
        {aiEnabled ? (
        <button onClick={onInfer} disabled={inferring}
                className="pf-btn-primary px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60"
                data-testid="products-infer-categories">
          {inferring ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
          {inferring ? "Inferring…" : "Categorise with AI"}
        </button>
        ) : <AiUpgradeCta testid="products-infer-locked" />}
      </div>

      {categories.items.map((cat) => {
        const isExp = !!expanded[cat.category];
        const selectedCount = cat.product_ids.filter((id) => selected.has(id)).length;
        const allSelected = selectedCount === cat.count && cat.count > 0;
        return (
          <div key={cat.category} className="pf-card overflow-hidden"
               data-testid={`products-cat-${cat.category}`}>
            <CategoryRowHeader
              cat={cat}
              isExp={isExp}
              allSelected={allSelected}
              selectedCount={selectedCount}
              onSelectAll={() => allSelected ? onDeselectCategory(cat.product_ids) : onSelectCategory(cat.product_ids)}
              onToggleExp={() => onExpand({ ...expanded, [cat.category]: !isExp })}
            />
            {isExp && (
              <div className="border-t border-slate-100 p-3 grid grid-cols-1 md:grid-cols-2 gap-2 bg-slate-50/40">
                {cat.product_ids.map((pid) => {
                  const p = productById[pid];
                  if (!p) return null;
                  const isSel = selected.has(pid);
                  return (
                    <div key={pid}
                         className={`pf-card p-3 flex gap-2 items-center bg-white transition ${isSel ? "ring-2 ring-cyan-500" : ""}`}
                         data-testid={`product-in-cat-${pid}`}>
                      <button onClick={() => onTogglePid(pid)} className="text-slate-400 hover:text-cyan-700"
                              data-testid={`product-in-cat-select-${pid}`}>
                        {isSel ? <CheckSquare className="w-3.5 h-3.5 text-cyan-600" /> : <Square className="w-3.5 h-3.5" />}
                      </button>
                      <div className="w-10 h-10 rounded bg-slate-100 overflow-hidden shrink-0 flex items-center justify-center">
                        {p.image ? <img src={p.image} alt="" className="w-full h-full object-cover" />
                                 : <ImageIcon className="w-4 h-4 text-slate-300" />}
                      </div>
                      <div className="flex-1 min-w-0 text-xs">
                        <div className="text-slate-900 font-medium truncate">{p.name}</div>
                        <div className="text-slate-500 tabular-nums">
                          {p.price_minor ? fmt(p.price_minor, p.currency) : "—"}
                          {p.active_offers > 0 && <span className="ml-2 text-emerald-700 font-semibold">· {p.active_offers} offers</span>}
                        </div>
                      </div>
                      <button onClick={() => onPerf(pid)} className="text-slate-400 hover:text-cyan-700 p-1" title="Performance">
                        <BarChart3 className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CreatorModal({ open, mode, onOpenChange, onDone }) {
  const [f, setF] = useState({ name: "", sku: "", price: "", category: "", image: "", url: "", description: "" });
  const [busy, setBusy] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    if (open) {
      setF({ name: "", sku: "", price: "", category: "", image: "", url: "", description: "" });
      setUrlInput(""); setSuggestions([]);
    }
  }, [open, mode]);

  const scrape = async () => {
    if (!urlInput) return toast.error("Paste a URL first.");
    setBusy(true);
    try {
      const r = await api.post("/v1/merchant/products/scrape", { url: urlInput });
      const e = r.data.extracted || {};
      setF({
        name: e.name || "", sku: "", price: e.price ? String(e.price) : "",
        category: "", image: e.image || "", url: urlInput,
        description: e.description || "",
      });
      setSuggestions(r.data.suggested_offers || []);
      toast.success("Details extracted. Review + save.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not scrape URL");
    } finally { setBusy(false); }
  };

  const save = async () => {
    if (!f.name) return toast.error("Product name is required.");
    setBusy(true);
    try {
      const r = await api.post("/v1/merchant/products", {
        name: f.name, sku: f.sku || null, price: f.price ? parseFloat(f.price) : null,
        currency: "CAD", image: f.image || null, url: f.url || null,
        category: f.category || null, description: f.description || null,
      });
      const newProduct = r.data.product;
      toast.success("Product added.");
      // If the merchant clicked a suggested offer, chain into campaign create.
      if (window.__pendingSuggestion && newProduct) {
        try {
          const s = window.__pendingSuggestion;
          await api.post("/merchant/campaigns", {
            name: s.description,
            campaign_type: s.discount_type === "cashback" ? "cashback" : "coupon",
            discount_type: s.discount_type === "cashback" ? "percent" : s.discount_type,
            discount_value: s.discount_value,
            commission_rate: 0,
            min_purchase: s.min_purchase,
            product_id: newProduct.id,
          });
          toast.success("Offer draft created — review it in Offers.");
        } catch (offerErr) {
          // The product itself saved successfully; the suggestion-draft is a
          // convenience. Keep the toast silent so we don't scare the merchant,
          // but log for backend observability.
          // eslint-disable-next-line no-console
          console.debug("[products] suggestion draft failed (product saved):",
                        offerErr?.message || offerErr);
        }
        window.__pendingSuggestion = null;
      }
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const pickSuggestion = (s) => {
    window.__pendingSuggestion = s;
    save();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="product-creator-modal">
        <DialogHeader>
          <DialogTitle>{mode === "url" ? "Paste a product URL" : "Add a product manually"}</DialogTitle>
          <DialogDescription>
            {mode === "url"
              ? "Cyannect will fetch the product's title, image, description and price so you don't have to retype them."
              : "Enter the essentials — you can always edit later."}
          </DialogDescription>
        </DialogHeader>
        {mode === "url" && (
          <div className="flex gap-2 items-center">
            <input value={urlInput} onChange={(e) => setUrlInput(e.target.value)}
                   placeholder="https://merchant.com/product/example"
                   className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                   data-testid="products-scrape-url" />
            <button onClick={scrape} disabled={busy}
                    className="pf-btn-primary px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-60"
                    data-testid="products-scrape-fetch">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Fetch
            </button>
          </div>
        )}
        {suggestions.length > 0 && (
          <div className="pf-card p-3 bg-gradient-to-r from-violet-50 to-cyan-50 border-cyan-200/60"
               data-testid="products-suggested-offers">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2 flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-cyan-500" /> One-click offer ideas
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {suggestions.map((s, i) => (
                <button key={i} onClick={() => pickSuggestion(s)}
                        disabled={busy || !f.name}
                        className="text-left pf-card p-3 hover:border-cyan-400 transition disabled:opacity-50"
                        data-testid={`products-suggested-${s.kind}`}>
                  <div className="text-[10px] uppercase tracking-widest text-cyan-700 font-semibold">{s.label}</div>
                  <div className="font-display text-slate-900 mt-1">
                    {s.discount_type === "percent" ? `${s.discount_value}% off` :
                     s.discount_type === "fixed" ? `CA$${s.discount_value} off` :
                     s.discount_type === "cashback" ? `${s.discount_value}% cashback` :
                     `${s.discount_value}`}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{s.description}</div>
                  {s.estimated_saving != null && (
                    <div className="text-[10px] text-emerald-700 font-semibold mt-1 tabular-nums">
                      ~ ${s.estimated_saving} saved
                    </div>
                  )}
                </button>
              ))}
            </div>
            <div className="text-[10px] text-slate-500 mt-2">Save the product first, then tap a suggestion to create the offer draft in one click.</div>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <FInput label="Name" value={f.name} onChange={(v) => setF({ ...f, name: v })} testid="products-f-name" />
          <FInput label="SKU" value={f.sku} onChange={(v) => setF({ ...f, sku: v })} testid="products-f-sku" />
          <FInput label="Price (CAD)" value={f.price} onChange={(v) => setF({ ...f, price: v })} type="number" testid="products-f-price" />
          <FInput label="Category" value={f.category} onChange={(v) => setF({ ...f, category: v })} testid="products-f-cat" />
          <FInput label="Image URL" value={f.image} onChange={(v) => setF({ ...f, image: v })} testid="products-f-image" />
          <FInput label="Product URL" value={f.url} onChange={(v) => setF({ ...f, url: v })} testid="products-f-url" />
        </div>
        <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mt-3">Description</label>
        <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={3}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  data-testid="products-f-desc" />
        <div className="flex justify-end gap-2 mt-3">
          <button onClick={() => onOpenChange(false)} className="px-4 py-2 rounded-full text-sm border border-slate-200 hover:bg-slate-50">Cancel</button>
          <button onClick={save} disabled={busy}
                  className="pf-btn-primary px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-60"
                  data-testid="products-save">
            <Plus className="w-3 h-3" /> {busy ? "Saving…" : "Add product"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BulkPromptModal({ open, onOpenChange, productIds, products, onDone }) {
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { if (open) { setPrompt(""); setResult(null); } }, [open]);

  const submit = async () => {
    if (prompt.length < 8) return toast.error("Describe the promotion in a bit more detail.");
    setBusy(true);
    try {
      const r = await api.post("/v1/merchant/offers/bulk-draft", {
        prompt, product_ids: productIds,
      });
      setResult(r.data);
      toast.success(`Created ${r.data.created} draft offer${r.data.created > 1 ? "s" : ""}.`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.detail || "Bulk draft failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bulk-prompt-modal">
        <DialogHeader>
          <DialogTitle>Bulk offer with AI</DialogTitle>
          <DialogDescription>
            Describe the promotion once — Cyannect creates one draft per selected product ({productIds.length} product{productIds.length > 1 ? "s" : ""}). All drafts land in Offers for your review.
          </DialogDescription>
        </DialogHeader>
        {!result ? (
          <>
            <div className="pf-card p-3 bg-slate-50 border-slate-200 max-h-40 overflow-y-auto">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Selected products</div>
              <div className="flex flex-wrap gap-1">
                {products.slice(0, 12).map((p) => (
                  <span key={p.id} className="pf-chip pf-chip-neutral text-xs">{p.name}</span>
                ))}
                {products.length > 12 && <span className="pf-chip pf-chip-neutral text-xs">+{products.length - 12} more</span>}
              </div>
            </div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mt-3">Describe the promotion</label>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4}
                      placeholder='e.g. "10% off from Friday to Sunday, minimum $50 order"'
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mt-1"
                      data-testid="bulk-prompt-input" />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => onOpenChange(false)} className="px-4 py-2 rounded-full text-sm border border-slate-200 hover:bg-slate-50">Cancel</button>
              <button onClick={submit} disabled={busy}
                      className="pf-btn-primary px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
                      data-testid="bulk-prompt-generate">
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                {busy ? `Drafting ${productIds.length}…` : `Generate ${productIds.length} drafts`}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="pf-card p-4 bg-gradient-to-r from-emerald-50 to-cyan-50 border-emerald-200/60"
                 data-testid="bulk-prompt-result">
              <div className="font-display text-lg text-slate-900">
                {result.created} draft{result.created > 1 ? "s" : ""} created ✨
              </div>
              <div className="text-xs text-slate-600 mt-1">
                Discount:{" "}
                <span className="font-semibold text-slate-900">
                  {result.parsed_offer.discount_type === "percent" ? `${result.parsed_offer.discount_value}%` :
                   result.parsed_offer.discount_type === "fixed" ? `CA$${result.parsed_offer.discount_value} off` :
                   result.parsed_offer.discount_type}
                </span>
                {result.parsed_offer.min_purchase && <> · Min <span className="font-semibold">CA${result.parsed_offer.min_purchase}</span></>}
                {result.parsed_offer.validity_days && <> · {result.parsed_offer.validity_days} day validity</>}
                {" · "} AI confidence <span className="font-semibold">{Math.round(result.parsed_offer.confidence * 100)}%</span>
              </div>
              <div className="mt-3 text-xs text-slate-500">
                Drafts are visible in <span className="text-cyan-700 font-semibold">Offers → pending</span>. Approve to make them active.
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={onDone}
                      className="pf-btn-primary px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1"
                      data-testid="bulk-prompt-done">
                Done <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PerformanceModal({ pid, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!pid) { setData(null); return; }
    setLoading(true);
    api.get(`/v1/merchant/products/${pid}/performance?days=30`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load performance"))
      .finally(() => setLoading(false));
  }, [pid]);

  return (
    <Dialog open={!!pid} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="product-perf-modal">
        <DialogHeader>
          <DialogTitle>{data?.product?.name || "Product performance"}</DialogTitle>
          <DialogDescription>Last {data?.window_days || 30} days across every offer attached to this product.</DialogDescription>
        </DialogHeader>
        {loading || !data ? (
          <div className="p-8 text-center text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Loading…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2">
              <PerfTile label="Views" value={(data.kpis.views || 0).toLocaleString()} tone="from-cyan-50 to-cyan-100/60" testid="perf-views" />
              <PerfTile label="Clicks" value={(data.kpis.clicks || 0).toLocaleString()} tone="from-violet-50 to-violet-100/60" testid="perf-clicks" />
              <PerfTile label="Activations" value={(data.kpis.activations || 0).toLocaleString()} tone="from-emerald-50 to-emerald-100/60" testid="perf-activations" />
              <PerfTile label="Conversions" value={(data.kpis.conversions || 0).toLocaleString()} tone="from-amber-50 to-amber-100/60" testid="perf-conversions" />
              <PerfTile label="Revenue" value={data.kpis.attributed_revenue_display || "$0.00"} tone="from-emerald-50 to-emerald-100/60" testid="perf-revenue" />
              <PerfTile label="Conv. rate" value={`${((data.kpis.conversion_rate || 0) * 100).toFixed(1)}%`} tone="from-slate-50 to-white" testid="perf-cvr" />
            </div>
            <div className="pf-card p-3 mt-3" data-testid="perf-recent">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Attached offers</div>
              {(data.recent_campaigns || []).length === 0 ? (
                <div className="text-xs text-slate-500">No offers attached to this product yet.</div>
              ) : (
                <div className="space-y-1">
                  {(data.recent_campaigns || []).slice(0, 5).map((c) => (
                    <div key={c.id} className="flex items-center justify-between py-1 text-xs border-b border-slate-50 last:border-0">
                      <div className="truncate">
                        <span className="text-slate-900 font-medium">{c.name}</span>
                        <span className="text-slate-500 ml-2">
                          {c.discount_value}{c.discount_type === "percent" ? "%" : ""} · {c.status}
                        </span>
                      </div>
                      <div className="tabular-nums text-emerald-700 font-semibold">{c.redemptions || 0}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PerfTile({ label, value, tone, testid }) {
  return (
    <div className={`pf-card p-3 bg-gradient-to-br ${tone}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="font-display text-lg text-slate-900 tabular-nums mt-1">{value}</div>
    </div>
  );
}

function FInput({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} type={type}
             className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mt-1"
             data-testid={testid} />
    </div>
  );
}
